<template>
  <form name="vpr-publications-form" @submit.prevent="$emit('save')">
    <v-layout v-if="!isCreate" class="sticky-panel">
      <v-spacer />
      <v-btn color="primary" :loading="skeleton" @click="onRefresh">
        Обновить
      </v-btn>
    </v-layout>

    <BLayoutExpansion title="Основное" class="my-4">
      <v-row class="d-flex justify-space-between mb-6 bg-surface-variant">
        <template v-if="isCreate">
          <v-col dense cols="6" fluid>
            <BAutocompleteCommon
              v-model="data.name"
              validate="required"
              $type="event-publications"
              @change="onPublicationSelected($event)"
            />
          </v-col>
        </template>
        <template v-else>
          <v-col dense cols="6" fluid>
            <v-text-field
              :loading="skeleton"
              :value="data.lk_publication_id"
              label="ID публикации"
              variant="regular"
              readonly
            />
          </v-col>
        </template>
      </v-row>
    </BLayoutExpansion>

    <PartComponentForm
      v-for="(part, idx) in data.parts"
      :ref="`part.${idx}`"
      :key="`${idx}.${part.vpr_publication_part_id}`"
      :skeleton="skeleton"
      v-bind="$props"
      :data="part"
      :lk-publication-id="data.lk_publication_id"
      :parts="data.parts"
      @remove="onRemovePart"
      @refresh="onRefresh"
      @update-part="($part) => onPartUpdated($part, idx)"
    />
    <button type="submit" class="d-none" />
  </form>
</template>

<script>
import { cloneDeep } from 'lodash'
import BLayoutForm from '@/components/layouts/BLayoutForm.vue'
import BLayoutExpansion from '@/components/layouts/BLayoutExpansion.vue'
import BAutocompleteCommon from '@/components/forms/BAutocompleteCommon.vue'
import PartComponentForm, { createDefaultData as createDefaultPartData } from './VPRPublicationsPartForm.vue'
import { VPRPublicationsApi } from '@/api/resources/Collector/VPRPublicationsApi'

const createDefaultData = () => ({
  vpr_publication_id: void 0,
  lk_publication_id: void 0,
  name: void 0,
  procedure_name: void 0,
  procedure_digit_code: void 0,
  parts: [createDefaultPartData()],
})


export default {
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  components: {
    BLayoutExpansion,
    PartComponentForm,
    BAutocompleteCommon,
  },
  extends: BLayoutForm,
  props: {
    skeleton: Boolean,
    loading: Boolean,
    data: {
      type: Object,
      required: true,
      default: createDefaultData,
    },
  },
  methods: {
    onPublicationSelected(topic) {
      if (topic === null) {
        this.data.lk_publication_id = null
        this.data.name = void 0
        this.data.procedure_digit_code = void 0
        this.data.parts = [createDefaultPartData()]
      } else {
        this.data.lk_publication_id = topic.id
        this.data.name = topic.name
        this.data.procedure_digit_code = topic.procedure.digit_code
      }
    },
    onAppendPart() {
      if (this.data.parts.length >= 10) {
        this.$notice.error('Максимальное кол-во добавленных частей: 10')
        return
      }

      const part = createDefaultPartData()
      part.position = this.data.parts.length + 1
      this.data.parts.push(part)
    },
    onRemovePart(position) {
      this.data.parts = this.data.parts.filter((el) => el.position !== position).map((el, idx) => ({
        ...el,
        position: idx + 1,
      }))
    },
    async onSave() {
      const result = await this.$validator.validateAll()

      if (!result) {
        if (this.data.lk_publication_id) {
          let idx = 1
          // eslint-disable-next-line no-restricted-syntax
          for (const part of this.data.parts) {
            if (!part.lk_kim_storage_id || !part.lk_krit_storage_id) {
              this.$notice.error(`Укажите хранилища для Части ${idx} или удалите ее`)
              return
            }
            // eslint-disable-next-line no-plusplus
            idx++
          }
        }
        this.$notice.validatorFormError(this)
        return
      }

      const api = new VPRPublicationsApi()
      try {
        const params = cloneDeep(this.data)
        delete params.procedure_name
        params.parts = params.parts.map((el) => {
          const r = { ...el }
          delete r.target_users
          delete r.generated_users
          delete r.kim_file_sha1
          delete r.krit_file_sha1
          delete r.vpr_publication_id
          delete r.vpr_publication_part_id
          delete r.xlsx_file_sha1
          return r
        })

        const { data } = await api.create(params)
        this.$emit('created:publication', data)
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoadingData = false
      }
    },
    onRefresh() {
      this.$emit('refresh', this.data.vpr_publication_id)
      this.data.parts.forEach((_, idx) => {
        let ref = this.$refs[`part.${idx}`]
        if (Array.isArray(ref)) {
          // eslint-disable-next-line prefer-destructuring
          ref = ref[0]
        }

        ref.onRefresh?.()
      })
    },
    onPartUpdated(part, idx) {
      this.data.parts[idx] = part
    },
  },
}
</script>

<style lang="scss" scoped>
.sticky-panel {
  position: sticky;
  top: 64px;
  z-index: 5;
  padding: 12px;
  background-color: $color-white;
  border-radius: 4px;
  box-shadow:
    0 3px 1px -2px rgba(0, 0, 0, 0.2),
    0 2px 2px 0 rgba(0, 0, 0, 0.14),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
}
</style>
